// Step-by-step spotlight overlay: dims the page, highlights the step's target, explains it.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, Check, Lightbulb, X } from "lucide-react";

const CARD_W = 400;

function useTarget(step, idx) {
  const [ready, setReady] = useState(false);
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let alive = true; let tries = 0; let clicked = false;
    setReady(false); setRect(null);
    const find = () => (step.target ? document.querySelector(step.target) : null);
    const tick = () => {
      if (!alive) return;
      if (step.click && !clicked && !find()) { const c = document.querySelector(step.click); if (c) { c.click(); clicked = true; } }
      const el = find();
      if (el || !step.target || tries > 30) { el?.scrollIntoView({ block: "center", behavior: "smooth" }); setReady(true); return; }
      tries += 1; setTimeout(tick, 150);
    };
    tick();
    return () => { alive = false; };
  }, [step, idx]);
  useEffect(() => {
    if (!ready) return undefined;
    const upd = () => {
      const r = step.target && document.querySelector(step.target)?.getBoundingClientRect();
      setRect(r && r.width > 0 ? { top: r.top, left: r.left, width: r.width, height: r.height } : null);
    };
    upd();
    const iv = setInterval(upd, 250);
    return () => clearInterval(iv);
  }, [ready, step]);
  return { ready, rect };
}

function cardPos(rect, h) {
  const vw = window.innerWidth; const vh = window.innerHeight;
  const m = vw < 640 ? 12 : 24;
  const w = Math.min(CARD_W, vw - m * 2);
  const corner = { left: vw - w - m, bottom: m, width: w };
  if (!rect) return { left: (vw - w) / 2, top: Math.max(m, (vh - h) / 2), width: w };
  if (rect.height > vh * 0.6) return corner;
  const left = Math.min(Math.max(m, rect.left), vw - w - m);
  if (rect.top + rect.height + 14 + h < vh - m) return { left, top: rect.top + rect.height + 14, width: w };
  if (rect.top - 14 - h > m) return { left, top: rect.top - 14 - h, width: w };
  return corner;
}

export default function TourOverlay({ tour, idx, setIdx, onClose, onFinish }) {
  const step = tour.steps[idx];
  const n = tour.steps.length;
  const last = idx === n - 1;
  const { ready, rect } = useTarget(step, idx);
  const cardRef = useRef(null);
  const [h, setH] = useState(320);
  useLayoutEffect(() => { if (cardRef.current) setH(cardRef.current.offsetHeight); }, [ready, idx, rect?.width]);
  useEffect(() => {
    const onKey = (e) => {
      if (["Escape", "ArrowRight", "ArrowLeft"].includes(e.key)) { e.stopPropagation(); e.preventDefault(); }
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") (last ? onFinish() : setIdx(idx + 1));
      if (e.key === "ArrowLeft" && idx > 0) setIdx(idx - 1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [idx, last, onClose, onFinish, setIdx]);
  const pad = 6;
  return createPortal(
    <div className="fixed inset-0 z-[2147483000]" data-testid="tour-overlay">
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />
      {rect ? (
        <div className="absolute rounded-xl pointer-events-none ring-2 ring-teal-300/90 transition-[top,left,width,height] duration-300 ease-out"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(2,6,23,0.74), 0 0 28px rgba(45,212,191,.35)" }}
          data-testid="tour-spotlight" />
      ) : <div className="absolute inset-0 bg-slate-950/75" />}
      {ready && (
        <div className="fixed z-[2147483001] rounded-2xl border border-white/10 bg-[#0d1526]/[0.97] backdrop-blur-xl shadow-2xl text-white tour-card-in"
          ref={cardRef} style={{ ...cardPos(rect, h), maxHeight: "calc(100vh - 24px)", overflowY: "auto" }} role="dialog" aria-label={step.title} data-testid="tour-card">
          <div className="h-1 rounded-t-2xl bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-teal-400 to-orange-400 transition-[width] duration-300" style={{ width: `${((idx + 1) / n) * 100}%` }} /></div>
          <div className="p-5">
            <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-widest text-teal-300/90">
              <span data-testid="tour-step-count">Step {idx + 1} of {n}</span><span className="text-slate-500">·</span><span className="truncate text-slate-400">{tour.title}</span>
              <button type="button" onClick={onClose} className="ml-auto p-1 -m-1 rounded text-slate-400 hover:text-white hover:bg-white/10" aria-label="Close tour" data-testid="tour-close"><X className="w-4 h-4" /></button>
            </div>
            <h3 className="mt-2 font-display text-[18px] font-bold leading-snug" data-testid="tour-step-title">{step.title}</h3>
            <div className="mt-2 space-y-2 text-[13px] leading-relaxed text-slate-300 max-h-[38vh] overflow-y-auto pr-1" data-testid="tour-step-body">
              {(step.body || "").split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {step.tip && (
              <div className="mt-3 flex gap-2 rounded-xl border border-orange-400/25 bg-orange-500/[0.08] p-3 text-[12.5px] text-orange-100" data-testid="tour-step-tip">
                <Lightbulb className="w-4 h-4 shrink-0 text-orange-300 mt-0.5" /><span><b className="text-orange-300">Sales tip · </b>{step.tip}</span>
              </div>
            )}
            {step.target && !rect && <div className="mt-2 text-[11px] text-slate-500">This part isn't on screen right now. It may need data or a pop-up open.</div>}
            <div className="mt-4 flex items-center gap-2">
              <button type="button" onClick={onClose} className="text-[12px] text-slate-400 hover:text-white" data-testid="tour-skip">Skip tour</button>
              <div className="ml-auto flex gap-2">
                {idx > 0 && <button type="button" onClick={() => setIdx(idx - 1)} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-white/15 text-[12.5px] text-slate-200 hover:bg-white/5" data-testid="tour-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>}
                <button type="button" onClick={() => (last ? onFinish() : setIdx(idx + 1))} data-testid={last ? "tour-finish" : "tour-next"}
                  className="inline-flex items-center gap-1 h-9 px-4 rounded-lg bg-teal-500 text-slate-950 text-[12.5px] font-bold hover:bg-teal-400 transition-colors">
                  {last ? <><Check className="w-3.5 h-3.5" /> Finish</> : <>Next <ArrowRight className="w-3.5 h-3.5" /></>}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
